import type Variant from '@modules/product/type/variant';
import type { PriceUnitEnum, ProductPricingTypeEnum } from 'generated/graphql';
import { isEcomPriceType } from './is-ecom-price-type';
import type CurrencyAmount from '../type/currency-amount';
import type { MeasurementSystem } from '@archipro-website/localisation';
import { toDisplayUnitPrice } from './measured-price';

interface VariantPriceType {
    isOnSale: boolean;
    isPriceRange: boolean;
    price: CurrencyAmount;
    retailPrice: CurrencyAmount;
    maxPrice: CurrencyAmount;
    priceUnit?: PriceUnitEnum | null;
    hasPrice: boolean;
}

/**
 * The selected variant's price as shown. Prices set per square, linear or
 * cubic metre are restated per the market's display unit; range and sale
 * checks run on the stored amounts.
 */
export const makeVariantPrice = (
    hasPrice: boolean,
    selectedVariant: Variant | null,
    pricingType: ProductPricingTypeEnum,
    measurementSystem: MeasurementSystem = 'metric',
): VariantPriceType => {
    const result: VariantPriceType = {
        hasPrice,
        isOnSale: false,
        isPriceRange: false,
        price: {
            Amount: 0,
            ShortFormat: '',
            Currency: '',
        },
        retailPrice: {
            Amount: 0,
            ShortFormat: '',
        },
        maxPrice: {
            Amount: 0,
            ShortFormat: '',
        },
        priceUnit: null,
    };

    if (selectedVariant) {
        result.isPriceRange =
            !isEcomPriceType(pricingType) &&
            hasPrice &&
            selectedVariant.Price.Amount !== selectedVariant.MaxPrice.Amount;
        result.isOnSale =
            selectedVariant.PriceType === 'SALE' ||
            selectedVariant.RetailPrice.Amount > selectedVariant.Price.Amount;
        const toDisplay = <T extends CurrencyAmount>(price: T): T =>
            toDisplayUnitPrice(
                price,
                selectedVariant.PriceUnit,
                measurementSystem,
            );
        result.price = toDisplay(selectedVariant.Price);
        result.retailPrice = toDisplay(selectedVariant.RetailPrice);
        result.maxPrice = toDisplay(selectedVariant.MaxPrice);
        result.priceUnit = selectedVariant.PriceUnit;
    }

    return result;
};
